import type { Region } from '../../types/report.types'

export const formatNumber = (number: number, options?: { abbreviated?: boolean }): string => {
  const abbreviated = options?.abbreviated ?? true

  //option 1: return the full numerical value.
  if (!abbreviated) {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0,
    }).format(number)
  }

  //option 2: abbreviate the numerical value K/M/B/T.
  const suffixes = ['', 'k', 'M', 'B', 'T']
  let suffixIndex = 0
  let value = number

  while (value >= 1000 && suffixIndex < suffixes.length - 1) {
    value /= 1000
    suffixIndex++
  }

  // Handle decimals
  let formattedValue = ''
  if (value >= 100) {
    formattedValue = Math.round(value).toString()
  } else if (value >= 10) {
    formattedValue = value.toFixed(1)
  } else if (value >= 1) {
    formattedValue = value.toFixed(1)
  } else if (value === 0) {
    formattedValue = '0'
  } else {
    formattedValue = value.toFixed(2)
  }

  // Remove trailing .0
  formattedValue = formattedValue.replace(/\.0$/, '')

  return formattedValue + suffixes[suffixIndex]
}

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

export const formatCurrencyRounded = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

export const replaceUnderscore = (str: string) => {
  if (!str) return ''
  return str.replace(/_/g, ' ')
}

export const capitalizeWords = (str: string) => {
  const underscoreReplaced = replaceUnderscore(str)
  return underscoreReplaced.replace(/\b\w/g, (l) => l.toUpperCase())
}

/**
 * Cleans up special value names for display purposes
 * @param value - The value to clean up
 * @returns The cleaned up display value
 */
export const cleanDisplayValue = (value: string): string => {
  if (value === 'NO_VALUE_SET') {
    return 'No value set'
  }
  if (value === 'NULL_VALUE') {
    return 'No value set'
  }
  return value
}

/**
 * Parses regions from report data, handling both new format (data_selection.regions) and old format (region string)
 * @param data - Report data object
 * @returns Array of region strings
 */
export const parseRegionsFromReportData = (data: {
  data_selection?: { regions?: string[]; region?: string }
  regions?: string[]
  region?: string
}): Region[] => {
  if (data?.data_selection?.regions && Array.isArray(data.data_selection.regions)) {
    return data.data_selection.regions as Region[]
  }
  if (data?.data_selection?.region) {
    return typeof data.data_selection.region === 'string' && data.data_selection.region.includes(',')
      ? (data.data_selection.region.split(',').map((r: string) => r.trim()) as Region[])
      : [data.data_selection.region as Region]
  }
  if (data?.regions && Array.isArray(data.regions) && data.regions.length > 0) {
    return data.regions as Region[]
  }
  if (data?.region) {
    return typeof data.region === 'string' && data.region.includes(',')
      ? (data.region.split(',').map((r: string) => r.trim()) as Region[])
      : [data.region as Region]
  }
  return []
}

export const formatHours = (hours: number): string => {
  if (!hours || hours === 0 || isNaN(hours)) return '0h 0m'
  const wholeHours = Math.floor(hours)
  const minutes = Math.round((hours - wholeHours) * 60)
  return `${wholeHours}h ${minutes}m`
}

export const parseDurationToHours = (v: any): number => {
  if (v === null || v === undefined) return NaN
  if (typeof v === 'number') return v / 3600 // seconds -> hours
  const s = String(v).trim()
  const cleaned = s.replace(/,/g, '')

  const isoMatch = cleaned.match(/P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?/i)
  if (isoMatch && (isoMatch[1] || isoMatch[2] || isoMatch[3] || isoMatch[4])) {
    const days = parseInt(isoMatch[1] || '0', 10) || 0
    const hours = parseInt(isoMatch[2] || '0', 10) || 0
    const mins = parseInt(isoMatch[3] || '0', 10) || 0
    const secs = parseInt(isoMatch[4] || '0', 10) || 0
    return days * 24 + hours + mins / 60 + secs / 3600
  }

  const dayMatch = cleaned.match(/(\d+)\s+days?,?\s*(\d{1,2}:\d{2}:\d{2})?/) || cleaned.match(/^(\d+)\s+(\d{1,2}:\d{2}:\d{2})$/)
  if (dayMatch) {
    const days = parseInt(dayMatch[1] || '0', 10) || 0
    const hms = dayMatch[2]
    if (hms) {
      const parts = hms.split(':').map((p) => parseInt(p || '0', 10) || 0)
      const [h, m, sec] = parts
      return days * 24 + h + m / 60 + sec / 3600
    }
    return days * 24
  }

  if (cleaned.includes(':')) {
    const parts = cleaned.split(':').map((p) => parseFloat(p) || 0)
    if (parts.length === 3) return parts[0] + parts[1] / 60 + parts[2] / 3600
    if (parts.length === 2) return parts[0] + parts[1] / 60
  }

  const n = parseFloat(cleaned)
  return isNaN(n) ? NaN : n
}

/**
 * Formats a pillar or region string to title case
 * @param str - The string to format (e.g., "e_marketplaces", "north_america")
 * @returns Formatted string (e.g., "E Marketplaces", "North America")
 */
export const formatPillarOrRegion = (str: string): string => {
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Formats a Date object to API date string format (YYYY-MM-DD)
 * @param date - The date to format
 * @returns Formatted date string
 */
export const formatDateForAPI = (date: Date): string => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Formats a date string to long display format (e.g., "March 13, 2026")
 */
export const formatDateLong = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Formats a date string to short display format (e.g., "Mar 13, 2026")
 */
export const formatDateShort = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Formats a date string to long date + time (e.g., "March 13, 2026, 02:30 PM")
 */
export const formatDateTimeLong = (dateString: string): string => {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Calculates duration between two timestamps and formats as "Xh Ym"
 */
export const formatTimeRange = (startTime: string, endTime: string): string => {
  const start = new Date(startTime)
  const end = endTime ? new Date(endTime) : new Date()
  const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60)
  if (!hours || hours === 0) return '0h 0m'
  const wholeHours = Math.floor(hours)
  const remainingMinutes = Math.round((hours - wholeHours) * 60)
  if (wholeHours === 0 && remainingMinutes === 0) return '0h 0m'
  return `${wholeHours}h ${remainingMinutes}m`
}

/**
 * Formats a Date to datetime-local input value (e.g., "2026-03-13T14:30")
 */
export const formatForDateTimeLocal = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

// Color palette for tags and visual elements
const COLORS = ['#B4EFFC', '#1DCFF6', '#5E58BE', '#AFABDE', '#090369']

/**
 * Gets a color from the palette based on string length
 * @param str - String to get color for
 * @returns Hex color code
 */
export const getColorFromPalette = (str: string): string => {
  return COLORS[str.length % COLORS.length]
}

/**
 * Gets appropriate text color (dark or light) based on background color
 * @param bgColor - Background color hex code
 * @returns Text color for contrast
 */
export const getContrastTextColor = (bgColor: string): string => {
  return bgColor === '#AFABDE' || bgColor === '#B4EFFC' ? 'neutral.900' : 'white'
}
